import useTable from '../../../components/Table';
import { VariableSizeList as List } from 'react-window';
import useWidth from '../../../hooks/useWidth';
import { useEffect, useCallback, useRef } from 'react';
import PayoutActivityRow from './PayoutActivityRow';
import { SortDirection } from '../../../components/Table/tableTypes';
import useUserReferralStats from '../../../hooks/referral/useUserReferralStats';

type PayoutActivityProps = {
    dataPerRow: number;
    rowMargin: number;
};

const PayoutActivity: React.FC<PayoutActivityProps> = ({ dataPerRow, rowMargin }) => {
    const windowWidth = useWidth();

    const listRef = useRef<List<any> | null>(null);

    const { userReferralStats } = useUserReferralStats();

    const { Table, activeSort } = useTable({
        data: userReferralStats?.payoutActivity,
        initialSortKey: 'timestamp',
    });

    const sortedData = userReferralStats?.payoutActivity
        ? userReferralStats.payoutActivity.sort((a, b) => {
              const direction = activeSort.direction === SortDirection.ASC ? 1 : -1;

              const aValue = parseFloat(a[activeSort.sortKey].toString());
              const bValue = parseFloat(b[activeSort.sortKey].toString());

              return (aValue - bValue) * direction;
          })
        : [];

    const getHeight = useCallback(() => {
        let height;

        // responsive card heights
        switch (true) {
            case windowWidth <= 768:
                height = 150;
                break;
            case windowWidth <= 900:
                height = 75;
                break;
            default:
                height = 97;
                break;
        }

        return height + rowMargin;
    }, [windowWidth, rowMargin]);

    // resets cache of rows
    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [windowWidth]);

    return (
        <Table className='payout-activity-table'>
            {!sortedData.length ? (
                <span className='no-payouts'>No payouts have been made yet...</span>
            ) : (
                <>
                    <div className='row header'>
                        <Table.Header sortKey='earnings' withSort>
                            Your Earnings
                        </Table.Header>
                        <Table.Header sortKey='tradeSize' withSort>
                            Trade Size
                        </Table.Header>
                        <Table.Header sortKey='timestamp' withSort>
                            Date
                        </Table.Header>
                        <Table.Header>TX ID</Table.Header>
                    </div>
                    <List
                        width={'100%'}
                        ref={listRef}
                        height={getHeight() * Math.min(sortedData.length, dataPerRow)}
                        itemCount={sortedData.length}
                        itemSize={getHeight}
                        className='row-container'
                    >
                        {({ index, style }) => {
                            return (
                                <PayoutActivityRow
                                    key={index}
                                    payout={sortedData[index]}
                                    style={{
                                        ...style,
                                        height: (style.height as number) - rowMargin,
                                    }}
                                />
                            );
                        }}
                    </List>
                </>
            )}
        </Table>
    );
};

export default PayoutActivity;
